import * as React from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { Form } from '@/components/ui/form'
import TextField from '@/components/forms/text-field'
import { Button } from '@/components/ui/button'
import { useCreatePlan } from '@/features/plans/api/create-plan'
import { useEditPlan } from '@/features/plans/api/edit-plan'
import { usePlan } from '@/features/plans/api/get-plan'
import TextAreaField from '@/components/forms/text-area-field'
import SelectField from '@/components/forms/select-field'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import NumberField from '@/components/forms/number-field'
import ChargeBeePlansDropdown from '@/features/subscriptions/components/ChargeBeePlansDropdown'

const FeatureSchema = z.object({
    featureKey: z.enum(['documents', 'ffls', 'users']),
    limitType: z.enum(['hard', 'soft']),
    limitInt: z.coerce.number().optional(),
    gracePercent: z.coerce.number().optional(),
    throttleAfter: z.coerce.number().optional(),
})

const PlanFormSchema = z.object({
    planId: z.string().optional(),
    planSku: z.string().min(1, 'SKU is required'),
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional().default(''),
    billingInterval: z.enum(['month', 'year']).default('month'),
    features: z.array(FeatureSchema).default([]),
    externalId: z.string().optional(),
})

type PlanFormValues = z.infer<typeof PlanFormSchema>

export default function PlanForm() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEdit = Boolean(id)
    const { data: existing } = usePlan({ planId: id as string, queryConfig: { enabled: isEdit } as any })
    const createPlan = useCreatePlan()
    const editPlan = useEditPlan()

    const normalizeInterval = (val?: string): 'month' | 'year' | undefined => {
        if (!val) return undefined
        const v = val.toLowerCase()
        if (v === 'month' || v === 'monthly') return 'month'
        if (v === 'year' || v === 'yearly') return 'year'
        return undefined
    }

    const normalizeLimitType = (val?: string): 'hard' | 'soft' => {
        return val && val.toLowerCase() === 'soft' ? 'soft' : 'hard'
    }

    const normalizeFeatureType = (val?: string): 'documents' | 'ffls' | 'users' => {
        const v = (val || '').toLowerCase()
        if (v === 'ffls') return 'ffls'
        if (v === 'users') return 'users'
        return 'documents'
    }

    const form = useForm<PlanFormValues>({
        resolver: zodResolver(PlanFormSchema) as any,
        mode: 'onBlur',
        defaultValues: {
            planId: existing?.planId ?? '',
            planSku: existing?.planSku ?? '',
            name: existing?.name ?? '',
            description: existing?.description ?? '',
            billingInterval: normalizeInterval(existing?.billingInterval) ?? 'month',

            features: Array.isArray(existing?.features) && existing!.features.length > 0
                ? existing!.features.map((pf: any) => ({
                    featureKey: normalizeFeatureType(pf?.featureKey),
                    limitType: normalizeLimitType(pf?.limitType),
                    limitInt: (pf?.limitInt as number) ?? 0,
                    gracePercent: (pf?.gracePercent as number) ?? 0,
                    throttleAfter: (pf?.throttleAfter as number) ?? 0,
                }))
                : [
                    {
                        featureKey: 'documents',
                        limitType: 'hard',
                        limitInt: 0,
                        gracePercent: 0,
                        throttleAfter: 0,
                    },
                ],
        },
    })

    const { fields, append, remove } = useFieldArray({ control: form.control, name: 'features' })

    React.useEffect(() => {
        if (existing) {
            form.reset({
                planId: existing.planId,
                planSku: existing.planSku,
                name: existing.name,
                description: existing.description,
                billingInterval: normalizeInterval(existing.billingInterval) ?? 'month',

                features: Array.isArray(existing.features)
                    ? existing.features.map((pf: any) => ({
                        featureKey: normalizeFeatureType(pf?.featureKey),
                        limitType: normalizeLimitType(pf?.limitType),
                        limitInt: (pf?.limitInt as number) ?? 0,
                        gracePercent: (pf?.gracePercent as number) ?? 0,
                        throttleAfter: (pf?.throttleAfter as number) ?? 0,
                    }))
                    : [],
            })
        }
    }, [existing])

    const handleSubmit = (values: PlanFormValues) => {
        if (isEdit && existing?.planId) {
            editPlan.mutate(
                {
                    id: existing.planId,
                    planSku: values.planSku,
                    name: values.name,
                    description: values.description,
                    billingInterval: values.billingInterval,

                    features: values.features as any,
                },
                { onSuccess: () => navigate('/plans') }
            )
        } else {
            createPlan.mutate(
                {
                    planSku: values.planSku,
                    name: values.name,
                    description: values.description,
                    billingInterval: values.billingInterval,

                    features: values.features as any,
                },
                { onSuccess: () => navigate('/plans') }
            )
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit as any)} className='space-y-6'>
                {isEdit && existing?.planId && (
                    <TextField label='Plan ID' name='planId' readOnly disabled />
                )}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <TextField label='SKU' name='planSku' placeholder='SKU-001' />
                    <TextField label='Name' name='name' placeholder='Pro' />
                    <div className='md:col-span-2'>
                        <TextAreaField label='Description' name='description' placeholder='Describe this plan…' />
                    </div>
                    <SelectField
                        label='Billing Interval'
                        name='billingInterval'
                        options={[
                            { label: 'Month', value: 'month' },
                            { label: 'Year', value: 'year' },
                        ]}
                        placeholder='Select interval'
                    />

                </div>
                <div>
                    <h5 className='text-lg font-medium'>External Plan</h5>
                    <ChargeBeePlansDropdown value={form.watch('externalId')} onChange={(value, plan) => form.setValue('externalId', value || '')} />
                </div>

                <div className='space-y-4'>
                    {fields.map((field, index) => {
                        const limitType = form.watch(`features.${index}.limitType` as const)
                        return (
                            <Card key={field.id}>
                                <CardHeader className='flex flex-row items-center justify-between'>
                                    <CardTitle className='text-base'>Feature {index + 1}</CardTitle>
                                    <Button
                                        type='button'
                                        variant='ghost'
                                        className='h-8 w-8 p-0'
                                        onClick={() => remove(index)}
                                        aria-label='Remove feature'
                                    >
                                        <X className='h-4 w-4' />
                                    </Button>
                                </CardHeader>
                                <CardContent>
                                    <div className='grid grid-cols-1 gap-4'>
                                        <SelectField
                                            label='Feature Type'
                                            name={`features.${index}.featureKey`}
                                            options={[
                                                { label: 'Documents', value: 'documents' },
                                                { label: 'FFLS', value: 'ffls' },
                                                { label: 'Users', value: 'users' },
                                            ]}
                                            placeholder='Select type'
                                        />
                                    </div>
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                        <TextField label='Limit' name={`features.${index}.limitInt`} placeholder='0' />
                                        <SelectField
                                            label='Limit Type'
                                            name={`features.${index}.limitType`}
                                            options={[
                                                { label: 'Hard', value: 'hard' },
                                                { label: 'Soft', value: 'soft' },
                                            ]}
                                            placeholder='Select type'
                                        />
                                        {limitType === 'soft' && (
                                            <>
                                                <NumberField label='Grace Percent' name={`features.${index}.gracePercent`} min={0} max={100} placeholder='0' percentage />
                                                <NumberField label='Throttle After' name={`features.${index}.throttleAfter`} placeholder='0' />
                                            </>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>
                <div className='border-t px-4 py-3'>
                    <div className='flex justify-center'>
                        <Button
                            type='button'
                            variant='ghost'
                            onClick={() =>
                                append({
                                    featureKey: 'documents',
                                    limitType: 'hard',
                                    limitInt: 0,
                                    gracePercent: 0,
                                    throttleAfter: 0,
                                })
                            }
                        >
                            <Plus className='mr-2 h-4 w-4' /> Add Feature
                        </Button>
                    </div>
                </div>


                <div className='flex items-center gap-2'>
                    <Button type='submit' disabled={form.formState.isSubmitting || !form.formState.isDirty}>Save</Button>
                    <Button type='button' variant='ghost' onClick={() => navigate('/plans')}>Cancel</Button>
                </div>

            </form>
        </Form>
    )
}


